import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, LIST, ADD, EDIT, DANGER, SUCCESS,
} from "const"
import { CcLibrarySet } from "server/models"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_CC_LIBRARY,
    StoreType,
    AccountSettings,
    ProjectSettings,
    CcLibrarySetList,
    CcLibrarySetFilters,

    LoadAccountSettings,
    LoadProjectSettings,
    SaveAccountSettings,
    SaveProjectSettings,

    CopyDefaultCcLibrarySets,
    LoadAllCcLibrarySets,
    SortCcLibrarySets,
    ChangeCcLibrarySetStatus,
    DeleteCcLibrarySet,
    GetMaxCcLibrarySets,

    setSystemAccountSettings,
    setProjectProjectSettings,
    setCcLibrarySetList,
    setCcLibrarySetIds,
    setCcLibrarySetSort,
    setCcLibrarySetFilters,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Feedback,
    Button,
    Loader,
    TableSearchWrapper,
    Table,
    SortRows,
    ChangeStatus,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import CcLibrarySetSearch from "client/pages/search/ccLibrarySetSearch"
import ImportSetList from "../importSetList"
import Columns from "./columns"
import ConfirmDeleteModule from "./confirmDeleteModule"

let accountSetNumber = 0
let librarySetNumber = 0

const SetList = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const { defaultLibraryAssigned } = project.id ? projectSettings.cc : accountSettings.cc

    const baseRoute = useSelector((state: StoreType) => state.ccLibrarySet.baseRoute)
    const setList = useSelector((state: StoreType) => state.ccLibrarySet.setList)
    const setIds = useSelector((state: StoreType) => state.ccLibrarySet.setIds)
    const sortColumn = useSelector((state: StoreType) => state.ccLibrarySet.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.ccLibrarySet.sortOrder)
    const filters = useSelector((state: StoreType) => state.ccLibrarySet.filters)
    const reloadList = useSelector((state: StoreType) => state.ccLibrarySet.reloadList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [importState, setImportState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (userData.accountId === 1) return

        if (!project.id && !accountSettings.cc.defaultLibraryAssigned) {
            setImportState(PENDING)
            LoadAccountSettings(userData.accountId)
            .then((settings) => {
                if (!settings.cc.defaultLibraryAssigned) {
                    CopyDefaultCcLibrarySets(userData.accountId, null, false)
                    .then(() => {
                        const newSettings: AccountSettings = deepCopy(settings)
                        newSettings.cc.defaultLibraryAssigned = true

                        SaveAccountSettings(userData.accountId, newSettings)
                        .then(() => {
                            dispatch(setSystemAccountSettings(newSettings))
                            setImportState(IDLE)
                        })
                    })
                }
            })
        }

        if (project.id && !projectSettings.cc.defaultLibraryAssigned) {
            setImportState(PENDING)
            LoadProjectSettings(project.id)
            .then((settings) => {
                if (!settings.cc.defaultLibraryAssigned) {
                    CopyDefaultCcLibrarySets(userData.accountId, project.id, accountSettings.cc.defaultLibraryAssigned)
                    .then(() => {
                        const newSettings: ProjectSettings = deepCopy(settings)
                        newSettings.cc.defaultLibraryAssigned = true

                        SaveProjectSettings(project.id, newSettings)
                        .then(() => {
                            dispatch(setProjectProjectSettings(newSettings))
                            setImportState(IDLE)
                        })
                    })
                }
            })
        }
    }, [])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllCcLibrarySets(userData.accountId, project.id).then((sets) => {
            accountSetNumber = 0
            librarySetNumber = 0
            Object.values(sets).forEach((set: CcLibrarySet) => {
                if (set.ownerAccount === userData.accountId) accountSetNumber += 1
                else librarySetNumber += 1
            })

            dispatch(setCcLibrarySetList(sets))
            setLoadState(IDLE)
        })
    }, [reloadList, defaultLibraryAssigned])

    useEffect(() => {
        const sortedIds = SortCcLibrarySets(Object.values(setList), sortColumn, sortOrder, filters)
        dispatch(setCcLibrarySetIds(sortedIds))
    }, [setList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof CcLibrarySet, order) => {
        dispatch(setCcLibrarySetSort(column, order))
    }

    const onFilter = (newFilters: CcLibrarySetFilters) => {
        dispatch(setCcLibrarySetFilters(newFilters))
    }

    const onStatusChange: ChangeStatus = (set: CcLibrarySet, callback) => {
        ChangeCcLibrarySetStatus(set).then((status) => {
            callback(() => {
                const setListCopy: CcLibrarySetList = deepCopy(setList)
                setListCopy[set.id] = {
                    ...setListCopy[set.id],
                    status,
                }
                dispatch(setCcLibrarySetList(setListCopy))
            })
        })
    }

    const onDelete: DeleteRow = (set: CcLibrarySet, callback) => {
        DeleteCcLibrarySet(set.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                if (set.ownerAccount === userData.accountId) accountSetNumber -= 1
                else librarySetNumber -= 1

                const setListCopy: CcLibrarySetList = setList ? deepCopy(setList) : {}
                delete setListCopy[set.id]
                dispatch(setCcLibrarySetList(setListCopy))
                dispatch(setCcLibrarySetIds(setIds.filter((id) => id !== set.id)))
            })
        })
    }


    // max number of library sets
    const {
        maxAccountSets,
        maxLibrarySets,
        feedbackAccountSets,
        feedbackLibrarySets,
    } = GetMaxCcLibrarySets(packageSettings, userData, accountSetNumber, librarySetNumber, lang)

    const maxAccountSetsReached = maxAccountSets !== null && accountSetNumber >= maxAccountSets
    const maxAccountSetsExceeded = maxAccountSets !== null && accountSetNumber > maxAccountSets

    const maxLibrarySetsReached = maxLibrarySets !== null && librarySetNumber >= maxLibrarySets
    const maxLibrarySetsExceeded = maxLibrarySets !== null && librarySetNumber > maxLibrarySets

    const columns = Columns()

    const listAccess = !project.id
        ? hasAccess(PRM_CC_LIBRARY, LIST)
        : hasProjectAccess(PRM_CC_LIBRARY, LIST)

    const addAccess = !project.id
        ? hasAccess(PRM_CC_LIBRARY, ADD)
        : hasProjectAccess(PRM_CC_LIBRARY, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.complianceSets}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    (loadState === PENDING || importState === PENDING)
                    && <Loader />
                }
                <TableSearchWrapper>
                    <CcLibrarySetSearch
                        id="ccLibrarySetList"
                        rightOffset={addAccess && !project.id}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && !project.id && (
                            <Button
                                variant="success"
                                disabled={maxAccountSetsReached}
                                onClick={() => dispatch(addModal({ url: `${baseRoute}/${EDIT}/0` }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={setList}
                    dataIds={setIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onStatusChange={onStatusChange}
                    onDelete={onDelete}
                    bottomContent={addAccess && userData.accountId !== 1 && (
                        <Button
                            variant="success"
                            disabled={maxLibrarySetsReached}
                            onClick={() => dispatch(addModal({
                                key: "importCcLibrarySet",
                                content: <ImportSetList />,
                            }))}
                        >
                            {lang.import}
                        </Button>
                    )}
                />
                {
                    !project.id && userData.accountId !== 1 && (
                        <PageForm>
                            <Feedback variant={maxAccountSetsExceeded ? DANGER : SUCCESS}>{feedbackAccountSets}</Feedback>
                            <Feedback variant={maxLibrarySetsExceeded ? DANGER : SUCCESS}>{feedbackLibrarySets}</Feedback>
                        </PageForm>
                    )
                }
            </PageContent>

            <ConfirmDeleteModule />
        </Container>
    )
}
export default SetList
